import { C } from "../constants";
import { toDisplayWeight, weightLabel } from "../utils/units";
import { fmtDate } from "../utils/dateHelpers";
import { MuscleBalanceCard } from "./MuscleBalanceCard";
import { LibraryIcon } from "./icons/LibraryIcon";

export const Dashboard = ({ dashStats, settings, startRun, onShowLibrary }) => (
  <>
    {/* Streak + This Week */}
    <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px", flex: 1 }}>
        <div style={{ color: C.muted, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Streak</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <div style={{ fontSize: 36, fontWeight: 800, color: dashStats.streak > 0 ? C.orange : C.text, lineHeight: 1 }}>{dashStats.streak}</div>
          <div style={{ fontSize: 13, color: C.muted }}>day{dashStats.streak !== 1 ? "s" : ""}</div>
        </div>
        <div style={{ color: C.muted, fontSize: 12, marginTop: 6 }}>{dashStats.streak === 0 ? "No active streak" : "Keep it going 🔥"}</div>
      </div>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px", flex: 1 }}>
        <div style={{ color: C.muted, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>This Week</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <div style={{ fontSize: 36, fontWeight: 800, color: C.text, lineHeight: 1 }}>{dashStats.weekSessions}</div>
          <div style={{ fontSize: 13, color: C.muted }}>session{dashStats.weekSessions !== 1 ? "s" : ""}</div>
        </div>
        <div style={{ color: C.muted, fontSize: 12, marginTop: 6 }}>Since Sunday</div>
      </div>
    </div>

    {/* Next Workout */}
    {dashStats.nextPlan && (
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 20px", marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ color: C.muted, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Next Workout</div>
          {dashStats.activeProgram && (
            <div style={{ fontSize: 11, color: C.blue, fontWeight: 600 }}>{dashStats.activeProgram.emoji} {dashStats.activeProgram.name}</div>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 22 }}>{dashStats.nextPlan.emoji}</span>
              <span style={{ fontWeight: 700, fontSize: 17, color: C.text }}>{dashStats.nextPlan.name}</span>
            </div>
            <div style={{ color: C.muted, fontSize: 12 }}>
              {dashStats.nextPlan.exercises.map(e => e.name).join(" · ")}
            </div>
          </div>
          <button onClick={() => startRun(dashStats.nextPlan)} style={{
            background: C.accent, color: "#1e1d1b", border: "none",
            borderRadius: 10, padding: "10px 20px", fontWeight: 700, fontSize: 14, cursor: "pointer", flexShrink: 0, marginLeft: 16,
          }}>▶ Start</button>
        </div>
      </div>
    )}

    {/* Quick actions */}
    {onShowLibrary && (
      <button onClick={onShowLibrary} style={{
        width: "100%", marginBottom: 16, padding: "13px 18px",
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
        color: C.text, fontSize: 14, fontWeight: 600, cursor: "pointer",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        fontFamily: "inherit",
      }}>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <LibraryIcon />
          <span>Exercise Library</span>
        </span>
        <span style={{ color: C.muted, fontSize: 13 }}>›</span>
      </button>
    )}

    {/* Muscle Balance */}
    <MuscleBalanceCard muscleBalance={dashStats.muscleBalance} maxMuscle={dashStats.maxMuscle} />

    {/* Recent PRs */}
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px", marginBottom: 16 }}>
      <div style={{ color: C.muted, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>
        Recent PRs <span style={{ fontSize: 10, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>— last 14 days</span>
      </div>
      {dashStats.recentPRs.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {dashStats.recentPRs.map(pr => {
            const dispW = toDisplayWeight(pr.weight, settings.weightUnit);
            const wl    = weightLabel(settings.weightUnit);
            return (
              <div key={pr.name + pr.date} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 28, height: 28, borderRadius: 7, background: C.blue + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, flexShrink: 0 }}>🏆</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{pr.name}</div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 1 }}>{fmtDate(pr.date)}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: C.blue }}>{dispW} {wl}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>× {pr.reps} reps</div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: "16px 0" }}>
          No PRs in the last 14 days — time to push it 💪
        </div>
      )}
    </div>
  </>
);
